// MusicDetailGalleryUI.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LazyMedia from "../../components/LazyMedia";
import FollowButtonInline from "../../components/FollowButtonInline";
import { useTranslation } from "react-i18next";
import { HLSPlayer } from "../Gallery/GalleryUIFunctions";
import api from "../../../shared/api/axios";
import Modal from "../../settings/Modal";

export type MediaEntry = {
  kind: 'image' | 'video';

  // image
  url?: string;

  // video pipeline
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // music
  musicTitle?: string | null;
  musicArtist?: string | null;
  musicId?: string | null;
};

export interface Post {
  _id: string;
  media: MediaEntry[];  // This is the key part, media should be an array of MediaEntry
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
}

/** ——— Local helpers/consts used by the UI components ——— */
  
// Api base URL normalization

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, "").replace(/\/$/, "");

const hlsUrl = (videoId: string) =>
  `${API_ORIGIN}/hls/playlist/${videoId}`;

const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return hlsUrl(m.videoId);
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url; // legacy fallback
  return "";
};

const IMAGE_BASE = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;
if (!IMAGE_BASE) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const resolveImageUrl = (url?: string | null): string => {
  if (!url) return "";
  if (/^(https?:|data:)/i.test(url)) return url;
  return `${IMAGE_BASE.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|gif|avif)$/i;
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|m3u8)$/i;

const looksLikeImageUrl = (url?: string | null) => {
  if (!url) return false;
  return IMAGE_EXT_RE.test(url);
};

const looksLikeVideoUrl = (url?: string | null) => {
  if (!url) return false;
  return VIDEO_EXT_RE.test(url) || url.includes("/hls/") || url.includes("/playlist/");
};
// Media sizing
const MEDIA_BOX = "calc(100dvh - 55px)"; // bigger than your old 117px gap
const MEDIA_HEIGHT = MEDIA_BOX;
const MAX_MEDIA_WIDTH = MEDIA_BOX;

// Styles (duplicated locally so Gallery.tsx doesn't need changes)
const postCardStyle: React.CSSProperties = {
  background:
  'linear-gradient(145deg, rgba(6, 11, 23, 0.95), rgba(4, 7, 15, 0.95))',  
  padding: "15px 20px 0px 20px",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  color: "#888",
};

const mediaScrollContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  overflowY: "hidden",
  gap: "0px",
  paddingTop: 6,
  marginLeft: "-3%",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  touchAction: "auto",
  overscrollBehaviorX: "none",
};

// Asset utils (lightweight, module-scoped)
const isVideoUrl = (u: string) => {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return (
      VIDEO_EXT_RE.test(pathname) ||
      pathname.endsWith(".m3u8") ||
      pathname.includes("/hls/") ||
      pathname.includes("/playlist/")
    );
  } catch {
    const s = u.toLowerCase();
    return (
      VIDEO_EXT_RE.test(s) ||
      s.endsWith(".m3u8") ||
      s.includes("/hls/") ||
      s.includes("/playlist/")
    );
  }
};

const seenPrefetch = new Set<string>();

const prefetchAsset = (url?: string) => {
  if (!url || seenPrefetch.has(url)) return;

  if (isVideoUrl(url)) {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    try {
      v.load();
    } catch {}
  } else {
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = url;
  }

  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
  } catch {}

  seenPrefetch.add(url);
};

/** ——— Hook: useMediaOrientation ———
 * Determines if a media URL (image/video) is landscape. For videos, reads metadata.
 */
export function useMediaOrientation(
  url: string,
  isVideo: boolean,
  onResult: (isLandscape: boolean | null) => void
) {
  React.useEffect(() => {
    let alive = true;

    if (!url) {
      onResult(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const handle = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onResult(w && h ? w > h : null);
        cleanup();
      };
      const err = () => {
        if (alive) onResult(null);
        cleanup();
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", handle);
        v.removeEventListener("error", err);
      };

      v.addEventListener("loadedmetadata", handle);
      v.addEventListener("error", err);
      try {
        v.load();
      } catch {}

      return () => {
        alive = false;
        cleanup();
      };
    } else {
      onResult(null);
    }
  }, [url, isVideo, onResult]);
}

/** ——— Component: ImageAutoFit ———
 * Loads the image to detect orientation, then applies portrait/landscape styles.
 */
export function ImageAutoFit({
  src,
  alt,
  portraitStyle,
  landscapeStyle,
  outerPortraitStyle,
  outerLandscapeStyle,
  onOrientationChange,
  onClick,
  clickable,
}: {
  src: string;
  alt?: string;
  portraitStyle: React.CSSProperties;
  landscapeStyle: React.CSSProperties;
  outerPortraitStyle?: React.CSSProperties;
  outerLandscapeStyle?: React.CSSProperties;
  onOrientationChange?: (isLandscape: boolean | null) => void;
  onClick?: () => void;
  clickable?: boolean;
}) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      const val = w && h ? w > h : null;
      setIsLandscape(val);
      onOrientationChange?.(val ?? null);
    };
    img.onerror = () => {
      if (alive) {
        setIsLandscape(null);
        onOrientationChange?.(null);
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src, onOrientationChange]);

  const style = isLandscape ? landscapeStyle : portraitStyle;
  const outerStyle = isLandscape ? outerLandscapeStyle : outerPortraitStyle;

  return (
    <div style={outerStyle}>
      <LazyMedia
        type="img"
        src={src}
        alt={alt || t('gallery.media.previewAlt')}
        style={{
          ...style,
          cursor: clickable ? "pointer" : undefined,
        }}
        onClick={onClick}
      />
    </div>
  );
}

/** ——— Component: MediaItem ———
 * One tile inside a post’s horizontal gallery. Handles image vs video rendering,
 * hover overlay for music, and orientation styles for images.
 */
export function MediaItem({
  postId,
  index,
  media,
  isActive,
  globalMuted,
  registerChild,
  openViewer,
  hoveredKey,
  handleHoverEnter,
  handleHoverLeave,
  onMusicClick,
}: {
  postId: string;
  index: number;
  media: MediaEntry;
  globalMuted: boolean;
  isActive: boolean;
  registerChild: (el: HTMLDivElement | null, i: number) => void;
  openViewer: (index: number) => void;
  hoveredKey: string | null;
  handleHoverEnter: (key: string) => void;
  handleHoverLeave: () => void;
  onMusicClick?: (entry: MediaEntry) => void;
}) {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isVideoLandscape, setIsVideoLandscape] = React.useState<boolean | null>(null);

  const isDefinitelyVideo =
    !!media.hlsUrl ||
    !!media.videoId ||
    looksLikeVideoUrl(media.url);

  const isDefinitelyImage =
    looksLikeImageUrl(media.url);

  const isVideo = isDefinitelyVideo && !isDefinitelyImage;

  const mediaUrl = isVideo
    ? resolveVideoUrl(media)
    : resolveImageUrl(media.url);

  const musicLabel = [media.musicTitle, media.musicArtist].filter(Boolean).join(" — ");

  const hoverKey = `${postId}-${index}`;
  
  return (
    <div
      ref={(el) => registerChild(el, index)}
      onMouseEnter={() => handleHoverEnter(hoverKey)}
      onMouseLeave={handleHoverLeave}
      style={{
        position: "relative",
        height: MEDIA_HEIGHT,
        overflow: "visible",
        paddingBottom: 40,
        marginLeft: "3%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollSnapAlign: "center",
        flex: "0 0 auto",
        width: "auto",
        maxWidth: MAX_MEDIA_WIDTH,
        boxSizing: "border-box",
      }}
    >
      {/* Media frame */}
  {/* Media frame OUTER — allows music to escape */}
<div
  style={{
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",

  }}
>

  {/* Media frame INNER — clips rounded corners */}
  <div
  style={{
    width: "100%",
    height: "100%",
    borderRadius: 12,
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  }}
>

{/* 🎬 VIDEO */}
{isVideo && mediaUrl && (

  isVideoLandscape ? (
    // LANDSCAPE STRUCTURE
    <div
  style={{
    position: "relative",
    display: "inline-block",
    borderRadius: 12,
  }}
>
      <HLSPlayer
        src={mediaUrl}
        muted={globalMuted}
        active={isActive}
        onOrientation={setIsVideoLandscape}
        isLandscape={true}
        style={{
          maxHeight: MEDIA_BOX,
          maxWidth: "100%",
          height: "auto",
          width: "auto",
          objectFit: "contain",
          borderRadius: 12,

        }}
      />

      {/* Music under video */}
 {musicLabel && hoveredKey === hoverKey && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if (media.musicId) {
        navigate(`/musicdetails/${media.musicId}`);
      } else {
        onMusicClick?.(media);
      }
    }}
    style={{
      position: "absolute",
      bottom: -22,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 11,
      color: "#807f7f",
      whiteSpace: "nowrap",
      cursor: "pointer",
      zIndex: 20,
      pointerEvents: "auto",
    }}
  >
    🎵 {musicLabel}
  </div>
)}
    </div>
  ) : (
    // PORTRAIT STRUCTURE (original behavior)
    <>
      <HLSPlayer
        src={mediaUrl}
        muted={globalMuted}
        active={isActive}
        onOrientation={setIsVideoLandscape}
        isLandscape={false}
        style={{
          maxHeight: MEDIA_BOX,
          maxWidth: "100%",
          height: "auto",
          width: "auto",
          objectFit: "contain",
        }}
      />

{musicLabel && hoveredKey === hoverKey && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if (media.musicId) {
        navigate(`/musicdetails/${media.musicId}`);
      } else {
        onMusicClick?.(media);
      }
    }}
    style={{
      position: "absolute",
      bottom: -20,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 11,
      color: "#807f7f",
      whiteSpace: "nowrap",
      cursor: "pointer",
      zIndex: 6,
      pointerEvents: "auto",
    }}
  >
    🎵 {musicLabel}
  </div>
  )}
 </>
  )
)}

{/* 🖼️ IMAGE */}
{!isVideo && mediaUrl && (
  <ImageAutoFit
  src={mediaUrl}
  alt={t("gallery.media.previewAlt")}
  portraitStyle={{
    height: MEDIA_BOX,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
  }}
  landscapeStyle={{
    width: MEDIA_BOX,  
    height: "auto",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  }}
  outerPortraitStyle={{
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  }}
  outerLandscapeStyle={{
    display: "flex",
    alignItems: "flex-start", 
    justifyContent: "center",
    width: "100%",
    height: "100%",
    paddingInline: 6,
  }}
  onClick={() => openViewer(index)}
  clickable
/>
        )}
    </div>
  </div>
</div>
 );
}

    

/** ——— Component: PostSection ———
 * Complete vertical page section for a single post (header, horizontal media strip, footer).
 */
export function PostSection({
  post,
  isActive,
  inWindow,
  globalMuted,
  openViewer,
  navigate,
  onLike,
  onUnlike,
  currentUserId,
  openPresentation,
  onMusicClick,
}: {
  post: Post;
  isActive: boolean;
  inWindow: boolean;
  globalMuted: boolean;
  openViewer: (media: MediaEntry[], index: number) => void;
  navigate: ReturnType<typeof useNavigate>;
  onLike: (id: string) => void;
  onUnlike: (id: string) => void;
  currentUserId?: string;
  openPresentation: (postId: string) => void;
  onMusicClick?: (entry: MediaEntry) => void; 
}) {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<HTMLDivElement[]>([]);
  const [currentIdx, setCurrentIdx] = React.useState(0);

  //Report Button
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [reportStep, setReportStep] = useState<"list" | "details" | "done">("list");
  const [details, setDetails] = useState("");

  const menuRef = useRef<HTMLDivElement | null>(null);

const handleReportPost = () => {
  setMenuOpen(false);
  setReportOpen(true);
  setReportStep("list");
};

const submitReport = async () => {
  if (!selectedReason) return;

  try {
    await api.post("/block-report/report/post", {
      postId: post._id,
      reasonKey: selectedReason,
      details: details || undefined,
    });

    setReportStep("done");

    setTimeout(() => {
      setReportOpen(false);
      setSelectedReason(null);
      setDetails("");
      setReportStep("list");
    }, 1200);
  } catch (err) {
    console.error(err);
    alert(t("gallery.report.failed"));
  }
};

useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (!menuRef.current) return;
    if (!menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
    }
  };

  if (menuOpen) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [menuOpen]);
// Report Button End

  // Prefetch neighbor media inside this post
 const getPrefetchUrl = (m?: MediaEntry) => {
  if (!m) return "";
  if (m.kind === "video") return resolveVideoUrl(m);
  return m.url || "";
};

React.useEffect(() => {
  const next = getPrefetchUrl(post.media[currentIdx + 1]);
  const prev = getPrefetchUrl(post.media[currentIdx - 1]);
  if (next) prefetchAsset(next);
  if (prev) prefetchAsset(prev);
}, [currentIdx, post.media]);

  // Profile navigation
  const ownerId = post.user?._id;
  const isOwnPost = !!ownerId && currentUserId === ownerId;
  const goToAuthor = () => {
    if (!ownerId) return;
    navigate(isOwnPost ? "/profile" : `/visitor-profile/${ownerId}`);
  };

  const goChatWithAuthor = () => {
  if (!post.user?._id || isOwnPost) return;

  navigate("/chat", {
    state: {
      toUserId: post.user._id,
      toUserName: post.user.profilename || "",
      toUserPicture: post.user.picture || "",
    },
  });

  setMenuOpen(false);
};
  // hover state for music pill
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  const hoverTimeoutRef = React.useRef<number | null>(null);
  const handleHoverEnter = (key: string) => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredKey(key);
  };
  const handleHoverLeave = () => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredKey(null);
      hoverTimeoutRef.current = null;
    }, 500);
  };
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const registerChild = (el: HTMLDivElement | null, i: number) => {
    if (el) itemRefs.current[i] = el;
  };

  // Track most-visible child in the horizontal scroller
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: { idx: number; ratio: number } | null = null;
        for (const e of entries) {
          const idx = itemRefs.current.findIndex((n) => n === e.target);
          if (idx >= 0) {
            const r = e.intersectionRatio;
            if (!best || r > best.ratio) best = { idx, ratio: r };
          }
        }
        if (best && best.ratio > 0) {
          requestAnimationFrame(() => setCurrentIdx(best!.idx));
        }
      },
      { root, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [post._id]);

  // Header right side info
 const countryCode = post.location?.countryCode?.toUpperCase();
const translatedCountry = countryCode
  ? t(`countries.${countryCode}`, {
      defaultValue: post.location?.country || countryCode,
    })
  : post.location?.country || "";

const locationLabel =
  post.location?.city && translatedCountry
    ? `${post.location.city}, ${translatedCountry}`
    : translatedCountry
    ? translatedCountry
   
    : "";

  const dateLabel = t("gallery.post.postedAt", {
    date: new Date(post.createdAt).toLocaleString(),
  });

  return (
    <section
      data-post-id={post._id}
      style={{
        ...postCardStyle,
        height: "100dvh",
        boxSizing: "border-box",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        display: "flex",
        flexDirection: "column",
        marginBottom: 0,
      }}
    >
      {/* SINGLE HEADER ROW */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          justifyContent: "space-between",
          flexWrap: "wrap", // still allows wrapping on very small screens
          marginBottom: 6,
        }}
      >
        {/* LEFT: avatar + name + follow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            onClick={goToAuthor}
            style={{
              width: 35,
              height: 35,
              borderRadius: "5%",
              backgroundColor: post.user?.picture ? "transparent" : "rgb(38, 29, 162)",
              backgroundImage: post.user?.picture ? `url(${post.user.picture})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: post.user?.picture ? 0 : "20px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {!post.user?.picture && "👻"}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              onClick={goToAuthor}
              style={{
                cursor: "pointer",
                color: "#bbb",
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: 220,
              }}
            >
              {post.user?.profilename || t("gallery.post.unknownUser")}
            </span>

 <span style={{ display: "inline-flex", flexShrink: 0, gap: 6 }}>
  <FollowButtonInline
    authorId={post.user?._id}
    profilename={post.user?.profilename}
  />

  {!isOwnPost && (
    <div ref={menuRef} style={{ position: "relative" }}>
    <button
      onClick={(e) => {
        e.stopPropagation();
        setMenuOpen((v) => !v);
      }}
      style={{
        background: "transparent",
        border: "none",
        color: "#bbb",
        fontSize: 18,
        cursor: "pointer",
        padding: "2px 6px",
      }}
    >
      ⋯
    </button>

{menuOpen && (
  <div
    style={{
      position: "absolute",
      left: "100%",
      top: "50%",
      transform: "translateY(-50%)",
      marginLeft: 8,
      background: "#111",
      border: "1px solid #333",
      borderRadius: 6,
      padding: "3px 5px",
      zIndex: 50,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
    }}
  >
    <button
      onClick={(e) => {
        e.stopPropagation();
        goChatWithAuthor();
      }}
      style={{
        backgroundColor: "#111",
        color: "#bbb",
        border: "1px solid #D4AF37",
        borderRadius: "6px",
        padding: "3px 12px",
        minWidth: "85px",
        textAlign: "center",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      💬 {t("MyLikes.buttons.chat")}
    </button>

    <button
      onClick={(e) => {
        e.stopPropagation();
        handleReportPost();
      }}
      style={{
        backgroundColor: "#111",
        color: "#bbb",
        border: "1px solid #D24444",
        borderRadius: "6px",
        padding: "3px 12px",
        minWidth: "85px",
        textAlign: "center",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      🚩 {t("gallery.report.button")}
    </button>
  </div>
)}
  </div>
  )}
</span>
          </div>
        </div>

        {/* RIGHT: location + date + buttons — all one line */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            fontSize: 13,
            marginRight: 70,
          }}
        >
          {/* location + date */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#555",
              whiteSpace: "nowrap",
            }}
          >
            {post.location?.city && (
  <button
    onClick={() => {
      const qs = new URLSearchParams({
        city: post.location?.city || "",
        country: post.location?.country || "",
        countryCode: post.location?.countryCode || "",
      }).toString();

      navigate(`/location-collection?${qs}`);
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#555",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
    title={locationLabel}
  >
    📍 {locationLabel}
  </button>
)}
            <span>{dateLabel}</span>
          </div>

          {/* buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => openPresentation(post._id)}
              style={{
                backgroundColor: "#111",
                color: "#bbb",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "3px 4.5px",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              🎬 {t("gallery.post.presentationButton")}
            </button>

            <button
              onClick={() =>
                post.likedByUser ? onUnlike(post._id) : onLike(post._id)
              }
              style={{
                backgroundColor: post.likedByUser ? "#ffccd5" : "#111",
                color: post.likedByUser ? "#a00" : "#bbb",
                border: "1px solid #ccc",
                borderRadius: "6px",
                padding: "3px 5px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {post.likedByUser
                ? `💔 Unlike (${post.likes})`
                : `❤️ Like (${post.likes})`}
            </button>
          </div>
        </div>
      </div>

      {/* MEDIA AREA (bigger, no big footer) */}
      {inWindow ? (
        <div ref={containerRef} style={mediaScrollContainerStyle}>
          {post.media.length === 0 ? (
            <p style={{ color: "#bbb" }}>{t("gallery.post.noMedia")}</p>
          ) : (
            post.media.map((m, index) => (
              <MediaItem
                key={`${m.videoId || m.hlsUrl || m.url || index}`}
                postId={post._id}
                index={index}
                media={m}
                isActive={isActive}
                globalMuted={globalMuted}
                registerChild={registerChild}
                openViewer={(i) => openViewer(post.media, i)}
                hoveredKey={hoveredKey}
                handleHoverEnter={handleHoverEnter}
                handleHoverLeave={handleHoverLeave}
                onMusicClick={onMusicClick}
              />
            ))
          )}
        </div>
      ) : (
        <div
          style={{
            height: MEDIA_HEIGHT,
            borderRadius: 12,
            background: "#050505",
          }}
        />
      )}

      {/* tiny spacer if you want a bit of breathing room */}

      <Modal
  open={reportOpen}
  onClose={() => {
    setReportOpen(false);
    setSelectedReason(null);
    setDetails("");
    setReportStep("list");
  }}
  title={t("gallery.report.title")}
  width={420}
>
  {reportStep === "list" && (
    <>
    {[
  { key: "spam", label: t("gallery.report.reasons.spam") },
  { key: "hate", label: t("gallery.report.reasons.hate") },
  { key: "harassment", label: t("gallery.report.reasons.harassment") },
  { key: "sexualContent", label: t("gallery.report.reasons.sexualContent") },
  { key: "scamOrFraud", label: t("gallery.report.reasons.scamOrFraud") },
  { key: "violence", label: t("gallery.report.reasons.violence") },
  { key: "selfHarm", label: t("gallery.report.reasons.selfHarm") },
  { key: "other", label: t("gallery.report.reasons.other") },
].map((r) => (
        <button
          key={r.key}
          onClick={() => {
            setSelectedReason(r.key);
            setReportStep("details");
          }}
          style={{
            width: "100%",
            padding: "10px",
            marginBottom: 8,
            background: "#111",
            border: "1px solid #333",
            borderRadius: 6,
            color: "#ddd",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {r.label}
        </button>
      ))}
    </>
  )}

  {reportStep === "details" && (
    <>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder={t("gallery.report.detailsPlaceholder")}
        style={{
          width: "100%",
          minHeight: 80,
          borderRadius: 6,
          padding: 10,
          background: "#000",
          border: "1px solid #333",
          color: "#fff",
          marginBottom: 12,
        }}
      />

      <button
        onClick={submitReport}
        style={{
          width: "100%",
          padding: "10px",
          background: "#ff4d4d",
          border: "none",
          borderRadius: 6,
          color: "#fff",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        {t("gallery.report.submit")}
      </button>
    </>
  )}

  {reportStep === "done" && (
    <div style={{ textAlign: "center" }}>
      ✅ {t("gallery.report.submitted")}
    </div>
  )}
</Modal>
    </section>
  );
}
 


export default {
  useMediaOrientation,
  ImageAutoFit,
  MediaItem,
  PostSection,

};
